import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ROUTE_PATHS } from '@smata-data-shared';
import { AuthStore } from '@smata-login-page';
import { NotificationFacade, NotificationPageActions } from '@smata-notification';

import { BehaviorSubject, Observable, catchError, filter, mergeMap, take, throwError } from 'rxjs';
import { AuthSessionStorage } from './auth-session.storage';
import { BYPASS_TOAST } from './bypass-toast.token';

const isRefreshing = signal(false);
const refreshTokenSubject$ = new BehaviorSubject<string | null>(null);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const notification = inject(NotificationFacade);
  const authStore = inject(AuthStore);

  const token = AuthSessionStorage.getToken();
  const authReq = token ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req;

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401) {
        const message = toErrorMessage(error);

        if (!req.context.get(BYPASS_TOAST)) {
          notification?.dispatch(NotificationPageActions.failure({ message }));
        }

        return throwError(() => error);
      }

      if (isRefreshing()) {
        return waitForTokenAndRetry(req, next);
      }

      isRefreshing.set(true);
      refreshTokenSubject$.next(null);

      return refreshAndRetry(req, next, router, notification, authStore);
    }),
  );
};

function waitForTokenAndRetry(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  return refreshTokenSubject$.pipe(
    filter((token): token is string => token !== null),
    take(1),
    mergeMap((token) => next(req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }))),
  );
}

function refreshAndRetry(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  router: Router,
  notification: NotificationFacade,
  authStore: AuthStore,
): Observable<HttpEvent<unknown>> {
  return authStore.refreshTokenBackgroundFlow().pipe(
    mergeMap((response) => {
      isRefreshing.set(false);
      const newToken = response?.data;

      if (!newToken) {
        handleLogoutRedirect(router, notification);
        return throwError(() => new Error('Session renewal failed.'));
      }

      AuthSessionStorage.setToken(newToken);
      refreshTokenSubject$.next(newToken);

      return next(req.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } }));
    }),
    catchError((refreshError) => {
      isRefreshing.set(false);
      refreshTokenSubject$.next(null);
      handleLogoutRedirect(router, notification);
      return throwError(() => refreshError);
    }),
  );
}

function handleLogoutRedirect(router: Router, notification: NotificationFacade): void {
  AuthSessionStorage.removeToken();
  router.navigate([ROUTE_PATHS.login]);
  notification?.dispatch(
    NotificationPageActions.failure({
      message: 'Your session has expired. Please log in again.',
    }),
  );
}

function toErrorMessage(error: HttpErrorResponse): string {
  switch (error.status) {
    case 0:
      return 'The server is unreachable. Please check your internet connection or if the backend is running.';
    case 403:
      return 'You do not have permission to access this resource.';
    case 404:
      return 'The requested resource was not found.';
    case 500:
      return 'Internal Server Error. Please try again later.';
    default:
      return error.error?.message ?? error.message ?? 'An unexpected error occurred.';
  }
}
